/**
 * MAX Bridge — обёртка над window.WebApp.
 *
 * См. docs/integrations/mini-app-scanner.md и
 * https://dev.max.ru/docs/webapps/bridge
 *
 * Если открыли вне MAX (dev в обычном браузере), Bridge молча возвращает
 * заглушку — UI работает, но кнопки MAX (MainButton/HapticFeedback) no-op.
 */

interface MaxThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
}

interface MaxUser {
  id: number | string;
  first_name?: string;
  last_name?: string;
  username?: string;
}

interface MaxWebApp {
  initData: string;
  initDataUnsafe: {
    auth_date?: number;
    hash?: string;
    user?: MaxUser;
    chat?: { id: number; type?: string };
    start_param?: string;
  };
  themeParams: MaxThemeParams;
  platform?: string;
  ready(): void;
  expand(): void;
  close(): void;
  enableClosingConfirmation?(): void;
  disableClosingConfirmation?(): void;
  showAlert?(msg: string): void;
  showPopup?(opts: unknown): void;
  HapticFeedback?: {
    notificationOccurred(kind: 'success' | 'warning' | 'error'): void;
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
  };
  MainButton?: {
    text: string;
    isVisible: boolean;
    show(): void;
    hide(): void;
    setText(text: string): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    enable(): void;
    disable(): void;
  };
  BackButton?: {
    isVisible: boolean;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
}

declare global {
  interface Window {
    WebApp?: MaxWebApp;
  }
}

let _bridge: MaxWebApp | null = null;
let _isMock = false;

export function initMaxBridge(): MaxWebApp {
  if (_bridge !== null) return _bridge;

  if (typeof window !== 'undefined' && window.WebApp) {
    _bridge = window.WebApp;
    try {
      _bridge.ready();
      _bridge.expand();
    } catch (err) {
      console.warn('MAX Bridge ready/expand failed', err);
    }
  } else {
    console.warn('MAX Bridge not found — running in mock mode (dev)');
    _bridge = createMockBridge();
    _isMock = true;
  }
  return _bridge;
}

export function getBridge(): MaxWebApp {
  if (_bridge === null) {
    return initMaxBridge();
  }
  return _bridge;
}

export function isMockMode(): boolean {
  return _isMock;
}

/**
 * Применить тему MAX через CSS-переменные, которые читает Tailwind.
 */
export function applyThemeParams(): void {
  const bridge = getBridge();
  const params = bridge.themeParams || {};
  const map: Record<string, string | undefined> = {
    '--max-bg': params.bg_color,
    '--max-text': params.text_color,
    '--max-hint': params.hint_color,
    '--max-link': params.link_color,
    '--max-button': params.button_color,
    '--max-button-text': params.button_text_color,
  };
  for (const [k, v] of Object.entries(map)) {
    if (v) document.documentElement.style.setProperty(k, v);
  }
}

/**
 * Получить initData (raw query string) для отправки на сервер в Authorization header.
 *
 * Согласно официальной документации MAX (https://dev.max.ru/docs/webapps/validation):
 *   "Извлекаем параметры платформы из фрагмента URL"
 *   const hashParams = new URLSearchParams(new URL(USER_LINK).hash.slice(1));
 *   const appData: string = hashParams.get('WebAppData') || '';
 *
 * То есть подписанная HMAC строка — это значение параметра `WebAppData` из
 * URL hash (`https://scan.inservo.ru/#WebAppData=...&WebAppPlatform=...`),
 * а не `window.WebApp.initData` через JS SDK.
 *
 * Проблема которая обнаружена 2026-05-16: `bridge.initData` иногда содержит
 * либо пустую строку, либо что-то отличное от `#WebAppData=`. Из-за этого
 * сервер видит invalid_signature.
 *
 * Стратегия: пробуем URL hash → window.WebApp.initData → пусто.
 */
export function getInitData(): string {
  // 1. Главный источник по доке MAX: URL hash #WebAppData=...
  if (typeof window !== 'undefined' && window.location.hash) {
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(hash);
    const webAppData = hashParams.get('WebAppData');
    if (webAppData) {
      return webAppData;
    }
  }

  // 2. Fallback на bridge.initData (старое поведение)
  const bridge = getBridge();
  return bridge.initData || '';
}

/**
 * Получить start_param из initData (например, n=444 — номер договора).
 */
export function getStartParam(): string | null {
  const bridge = getBridge();
  return bridge.initDataUnsafe?.start_param || null;
}

/**
 * Получить number из start_param.
 *
 * Поддерживаемые форматы (исторические + текущий):
 *   - «n_NNN» — current, MAX deeplink ?startapp=n_NNN (с 2026-05-18, после
 *     ответа MAX support: подчёркивание вместо `=` чтобы пройти proto.payload).
 *   - «n=NNN»  — legacy, до запрета payload в OpenAppButton.
 *   - «?n=NNN» в query — legacy fallback периода ADR-016 r2.
 */
export function getStartParamNumber(): number | null {
  const param = getStartParam();
  if (param) {
    // n_NNN — приоритет (current)
    const matchUnder = param.match(/n_(\d{1,5})/);
    if (matchUnder) return Number(matchUnder[1]);
    // n=NNN — legacy
    const matchEq = param.match(/n=(\d{1,5})/);
    if (matchEq) return Number(matchEq[1]);
  }
  // Legacy fallback: query-string ?n=NNN
  const urlN = new URLSearchParams(window.location.search).get('n');
  if (urlN && /^\d{1,5}$/.test(urlN)) {
    return Number(urlN);
  }
  return null;
}

/**
 * TASK-017: получить режим работы из start_param или query-string.
 * Форматы: «n_NNN_replace» (current), «mode=replace» в start_param (legacy),
 * `?mode=replace` в URL (legacy).
 */
export function getStartParamMode(): 'new' | 'replace' {
  const param = getStartParam();
  if (param) {
    if (/_replace\b/i.test(param)) return 'replace';
    if (/mode=replace/i.test(param)) return 'replace';
  }
  const urlMode = new URLSearchParams(window.location.search).get('mode');
  if (urlMode === 'replace') return 'replace';
  return 'new';
}

// ─── Mock для dev в обычном браузере ─────────────────────────────────────

function createMockBridge(): MaxWebApp {
  return {
    initData: '',
    initDataUnsafe: {
      user: { id: 'test-vyezdnoy-1', first_name: 'Dev', last_name: 'User' },
    },
    themeParams: {
      bg_color: '#ffffff',
      text_color: '#000000',
      hint_color: '#666666',
      button_color: '#2481cc',
      button_text_color: '#ffffff',
    },
    platform: 'mock',
    ready() {},
    expand() {},
    close() {
      console.log('[mock] WebApp.close()');
    },
    showAlert(msg: string) {
      window.alert(msg);
    },
    HapticFeedback: {
      notificationOccurred(kind) {
        console.log('[mock] haptic', kind);
      },
      impactOccurred(style) {
        console.log('[mock] impact', style);
      },
    },
    // MainButton/BackButton эмулируем как обычные кнопки внизу — без нативной отрисовки
    MainButton: createMockButton('MainButton'),
    BackButton: createMockButton('BackButton'),
  };
}


// TASK-006 W1+W3: Hybrid auth detect.
// Определяет как пользователь открыл страницу — для выбора flow
// (MAX-Mini-App с initData/HMAC, либо magic-link в обычном браузере).
export type AuthMode = 'max_initdata' | 'cookie' | 'magic_link';

export function detectAuthMode(): AuthMode {
  // 1. Есть session_jwt cookie от предыдущего magic-link login
  if (typeof document !== 'undefined' && document.cookie.includes('session_jwt=')) {
    return 'cookie';
  }
  // 2. Открыто из MAX-клиента (есть initData или #WebAppData=)
  if (typeof window !== 'undefined') {
    const hasWebAppData = window.location.hash.includes('WebAppData=');
    const hasBridgeInit = !!(window.WebApp && window.WebApp.initData);
    if (hasWebAppData || hasBridgeInit) {
      return 'max_initdata';
    }
  }
  // 3. Иначе — обычный браузер, magic-link flow
  return 'magic_link';
}


function createMockButton(label: string) {
  const handlers: Array<() => void> = [];
  return {
    text: '',
    isVisible: false,
    show() {
      console.log(`[mock] ${label}.show()`);
      this.isVisible = true;
    },
    hide() {
      this.isVisible = false;
    },
    setText(text: string) {
      this.text = text;
    },
    onClick(cb: () => void) {
      handlers.push(cb);
    },
    offClick(cb: () => void) {
      const idx = handlers.indexOf(cb);
      if (idx >= 0) handlers.splice(idx, 1);
    },
    enable() {},
    disable() {},
  };
}
